import type { CalendarLike } from './calendar'
import { workingDaysBetween } from './calendar'
import { HEADER_H, MILESTONE_SIZE, ROW_H } from './constants'
import { diffDays, type ISODate } from './format'
import { barBox, type BarBox } from './geometry'
import type { VisibleRow } from './tree'
import type { ScheduleActivity } from '../types'

/**
 * Pure arithmetic for Task 18's bar resize and link drag (spec B §9.6): kept out of
 * `hooks/useBarDrag.ts` so the hit-testing and preview math can be unit-tested without React or
 * pointer events, mirroring `lib/geometry.ts`/`lib/calendar.ts`.
 */

/** Content-coordinate pointer X → the resize drag's `finishDay`: the day index under the pointer,
 * clamped so the activity never shrinks to zero or negative duration (`minDay` = the activity's
 * own start day). */
export const resizeFinishDayAt = (contentX: number, pxPerDay: number, minDay: number): number =>
  Math.max(minDay, Math.floor(contentX / pxPerDay))

/** The activity's new duration for a resize preview: working days when a calendar is known (a
 * Mon–Fri calendar excludes the weekend from the count), calendar days otherwise. */
export const computeResizePreview = (cal: CalendarLike | null, startIso: ISODate, finishIso: ISODate): number =>
  cal ? workingDaysBetween(cal, startIso, finishIso) : diffDays(startIso, finishIso) + 1

/** The clickable/droppable footprint of a bar. `barBox` gives a milestone zero width at its centre
 * point, so this widens it to the diamond's actual footprint; a task/summary bar is widened to the
 * same 2px floor `TaskBar`/`SummaryBar` already render with. Shared by the link drag's hit test and
 * its target highlight so both agree on the same bounds. */
export const activityHitBox = (a: { start: ISODate; finish: ISODate; isMilestone: boolean }, rangeStart: ISODate, pxPerDay: number): BarBox => {
  const box = barBox(a, rangeStart, pxPerDay)
  if (!a.isMilestone) return { x: box.x, width: Math.max(2, box.width) }
  const half = MILESTONE_SIZE / 2
  return { x: box.x - half, width: MILESTONE_SIZE }
}

/** The visible row index under a content-coordinate Y, or `null` above the header / below the last row. */
export const rowIndexAtY = (contentY: number, rowCount: number): number | null => {
  const index = Math.floor((contentY - HEADER_H) / ROW_H)
  return index >= 0 && index < rowCount ? index : null
}

/** Live link-drop target (Step 3): the row under `contentY`, excluding `excludeId` and summaries.
 * Ghosts are never matched here — `rows` only ever holds local tree rows, so this always resolves
 * to a row's real, local activity; a ghost drawn on that row (§8.6) sits at a different x (its own
 * schedule's dates) and simply won't fall inside the local activity's box. */
export const hitTestLinkTarget = (
  rows: VisibleRow<ScheduleActivity>[],
  contentX: number,
  contentY: number,
  rangeStart: ISODate,
  pxPerDay: number,
  excludeId: string,
): string | null => {
  const index = rowIndexAtY(contentY, rows.length)
  if (index == null) return null
  const a = rows[index].node.activity
  if (a.id === excludeId || a.isSummary) return null
  const box = activityHitBox(a, rangeStart, pxPerDay)
  if (contentX < box.x || contentX > box.x + box.width) return null
  return a.id
}

/** Whether a link-drag drop at `(contentX, contentY)` landed on an explicitly refused target — the
 * source's own bar, a summary's bar, or a cross-schedule ghost (§8.6) — as opposed to empty
 * background, which should not toast. `hitTestLinkTarget` alone can't tell the two apart: both
 * return `null` (a ghost's foreign geometry never matches the local activity's box either), so this
 * checks the excluded activity's and any ghost's own footprint directly. */
export const isRefusedLinkDrop = (
  rows: VisibleRow<ScheduleActivity>[],
  ghostsByRow: Map<string, { start: ISODate; finish: ISODate; isMilestone: boolean }[]>,
  contentX: number,
  contentY: number,
  rangeStart: ISODate,
  pxPerDay: number,
  fromId: string,
): boolean => {
  const index = rowIndexAtY(contentY, rows.length)
  if (index == null) return false
  const a = rows[index].node.activity
  const within = (box: BarBox) => contentX >= box.x && contentX <= box.x + box.width
  if ((a.id === fromId || a.isSummary) && within(activityHitBox(a, rangeStart, pxPerDay))) return true
  const ghosts = ghostsByRow.get(a.id) ?? []
  return ghosts.some((g) => within(activityHitBox(g, rangeStart, pxPerDay)))
}
